import axios from 'axios';
import { samples } from './data';
// Fictional preview is restricted to loopback even if a remote build is misconfigured.
export const previewMode = process.env.REACT_APP_INQUIRIES_PREVIEW === 'true' && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
const key = 'diam_inquiries_fictional_preview_v3';
const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(key));
    if (Array.isArray(saved)) return saved.map(item => ({ ...samples.find(x => x._id === item._id), ...item }));
    return [];
  } catch { return []; }
};
const write = items => localStorage.setItem(key, JSON.stringify(items));
export async function listInquiries() {
  if (previewMode) return {
    items: read(),
    total: read().length
  };
  const items = [];
  let cursor, snapshot, total = 0;
  do {
    const data = (await axios.get('/api/inquiries', { timeout: 15000, params: { limit: 200, ...(cursor ? { cursor } : {}), ...(snapshot ? { snapshot } : {}) } })).data;
    total = data.total; items.push(...data.items);
    cursor = data.nextCursor; snapshot = data.snapshot;
  } while (cursor);
  items.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  return { items, total };

}
export async function saveInquiry(item, updates) {
  if (previewMode) {
    const items = read();
    const current = items.find(x => x._id === item._id);
    if (!current || current.version !== item.version) throw new Error('הפנייה עודכנה במקביל. יש לרענן.');
    const safeUpdates = { ...updates };
    for (const [text, translation] of [['transcript', 'transcriptTranslation'], ['suggestionFirst', 'suggestionFirstTranslation'], ['suggestionSecond', 'suggestionSecondTranslation'], ['suggestionThird', 'suggestionThirdTranslation'], ['customReply', 'customReplyTranslation'], ['responseText', 'responseTranslation']]) {
      if (safeUpdates[text] !== undefined && safeUpdates[text] !== current[text] && safeUpdates[translation] === undefined) safeUpdates[translation] = '';
    }
    const { fingerprint } = require('./approval');
    if (fingerprint({ ...current, ...safeUpdates }) !== fingerprint(current)) {
      if (['processing', 'waiting', 'completed', 'sent', 'unknown'].includes(current.jobState)) throw new Error('נדרשת בדיקת תוצאת הטיפול הקודם');
      safeUpdates.approval = null; safeUpdates.jobState = undefined;
    }
    const saved = {
      ...current,
      ...safeUpdates,
      version: current.version + 1,
      updatedAt: new Date().toISOString()
    };
    write(items.map(x => x._id === saved._id ? saved : x));
    return saved;
  }
  return (await axios.patch(`/api/inquiries/${item._id}`, {
    ...updates,
    version: item.version
  }, { timeout: 15000 })).data;
}
export async function createInquiry(data, clientRequestId) {
  if (previewMode) {
    const item = {
      ...data,
      _id: `demo-${Date.now()}`,
      version: 0,
      updatedAt: new Date().toISOString()
    };
    write([item, ...read()]);
    return item;
  }
  return (await axios.post('/api/inquiries', { ...data, ...(clientRequestId ? { clientRequestId } : {}) }, { timeout: 15000 })).data;
}

export async function approveInquiry(item, updates) {
  if (previewMode) {
    const { buildApproval } = require('./approval');
    const current = read().find(x => x._id === item._id);
    if (!current || current.version !== item.version) throw new Error('הפנייה השתנתה. יש לרענן ולאשר מחדש.');
    if (require('./approval').isHeld(current) || ['completed', 'no_reply'].includes(current.status) || ['processing', 'waiting', 'completed', 'sent', 'unknown'].includes(current.jobState)) throw new Error('נדרשת בדיקת תוצאת הטיפול הקודם');
    const saved = await saveInquiry(item, updates);
    saved.approval = buildApproval(saved);
    delete saved.jobState;
    write(read().map(x => x._id === saved._id ? saved : x));
    return saved;
  }
  return (await axios.post(`/api/inquiries/${item._id}/approve`, { ...updates, version: item.version }, { timeout: 15000 })).data;
}
export async function processApproved(items) {
  if (previewMode) {
    const { approvalCurrent, isHeld } = require('./approval');
    const ids = new Set(items.map(x => x._id));
    let prepared = 0;
    write(read().map(item => {
      if (!ids.has(item._id) || isHeld(item) || ['completed', 'no_reply'].includes(item.status) || !approvalCurrent(item) || item.jobState) return item;
      prepared += 1;
      return { ...item, jobState: 'queued', version: item.version + 1, updatedAt: new Date().toISOString() };
    }));
    return { prepared, sent: 0 };
  }
  let prepared = 0;
  for (let start = 0; start < items.length; start += 200) {
    const data = (await axios.post('/api/inquiries/process/approved', { ids: items.slice(start, start + 200).map(x => x._id) }, { timeout: 15000 })).data;
    prepared += data.prepared;
  }
  return { prepared, sent: 0 };
}
export async function queueSelected(items) {
  if (previewMode) {
    const { canQueueForReview } = require('./approval');
    const ids = new Set(items.map(x => x._id));
    let prepared = 0;
    write(read().map(item => {
      if (!ids.has(item._id) || !canQueueForReview(item)) return item;
      const selection = items.find(x => x._id === item._id);
      if (selection.version !== item.version || require('./approval').fingerprint(selection) !== require('./approval').fingerprint(item)) return item;
      prepared += 1;
      return { ...item, jobState: 'queued_review', version: item.version + 1, updatedAt: new Date().toISOString() };
    }));
    return { prepared, sent: 0 };
  }
  let prepared = 0, skipped = 0;
  const batchIds = [];
  for (let start = 0; start < items.length; start += 200) {
    const data = (await axios.post('/api/inquiries/queue/selected', { items: items.slice(start, start + 200).map(x => ({ id: x._id, version: x.version, fingerprint: require('./approval').fingerprint(x) })) }, { timeout: 15000 })).data;
    prepared += data.prepared; skipped += data.skipped || 0;
    if (data.prepared && data.batchId) batchIds.push(data.batchId);
  }
  return { prepared, skipped, batchIds, sent: 0 };
}

export async function readOperation(item) {
  if (previewMode) return { item, version: item.version, approval: item.approval || null, current: require('./approval').approvalCurrent(item), jobState: item.jobState || null, operation: item.operation || null };
  return (await axios.get(`/api/inquiries/${item._id}/operation`, { timeout: 15000 })).data;
}
export async function startOperation(item, fingerprint) {
  if (previewMode) throw new Error('קליטת מפעיל זמינה רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/operation/start`, { version: item.version, fingerprint }, { timeout: 15000 })).data;
}
// Explicit owner-confirmed send of the exact approved text through Beds24. Never called automatically.
export async function sendViaBeds24(item, ownerConfirmed) {
  if (previewMode) throw new Error('שליחה זמינה רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/beds24/send`, { version: item.version, operationId: item.operation?.id, text: item.approval?.text, ownerConfirmed: ownerConfirmed === true }, { timeout: 30000 })).data;
}
export async function recordOperationResult(item, outcome, evidence, summary, followUpRequired, followUpAssessment) {
  if (previewMode) throw new Error('תיעוד תוצאה זמין רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/operation/result`, { version: item.version, operationId: item.operation.id, fingerprint: item.approval.fingerprint, outcome, evidence, summary, followUpRequired, followUpAssessment }, { timeout: 15000 })).data;
}

export async function resolveFollowUp(item, assessment, evidence) {
  if (previewMode) throw new Error('תיעוד סיום מעקב זמין רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/follow-up/resolve`, { version: item.version, assessment, evidence }, { timeout: 15000 })).data;
}

export async function setOwnerHold(item, reason) {
  if (previewMode) throw new Error('המתנה לבעלים זמינה רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/hold`, { version: item.version, reason }, { timeout: 15000 })).data;
}
export async function releaseOwnerHold(item, reason) {
  if (previewMode) throw new Error('שחרור המתנה זמין רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/hold/release`, { version: item.version, holdId: item.ownerHold?.id || 'legacy-owner', reason, ownerAuthorized: true }, { timeout: 15000 })).data;
}

export async function reconcileOperation(item, outcome, evidence, summary, followUpRequired, followUpAssessment) {
  if (previewMode) throw new Error('בירור תוצאה קודמת זמין רק במערכת המאומתת');
  return (await axios.post(`/api/inquiries/${item._id}/operation/reconcile`, { version: item.version, operationId: item.operation.id, fingerprint: item.operation.fingerprint, outcome, evidence, summary, followUpRequired, followUpAssessment, verifiedWithoutRetry: true }, { timeout: 15000 })).data;
}


// Chat handoff is a server-verified contract. Preview never fabricates human authorization.
export async function prepareOwnerRequest(batchId) {
  if (previewMode) throw new Error('אימות בקשת בעלים אינו זמין בהדגמה');
  return (await axios.post('/api/inquiries/owner-requests/prepare', { batchId }, { timeout: 15000 })).data;
}
export async function readOwnerRequest(id) {
  if (previewMode) throw new Error('אימות בקשת בעלים אינו זמין בהדגמה');
  return (await axios.get(`/api/inquiries/owner-requests/${id}`, { timeout: 15000 })).data;
}
export async function verifyOwnerRequest(id, threadId, messageId) {
  if (previewMode) throw new Error('אימות בקשת בעלים אינו זמין בהדגמה');
  return (await axios.post(`/api/inquiries/owner-requests/${id}/verify`, { threadId, messageId }, { timeout: 15000 })).data;
}
export async function claimOwnerRequest(id, entry) {
  if (previewMode) throw new Error('קליטת מפעיל אינה זמינה בהדגמה');
  return (await axios.post(`/api/inquiries/owner-requests/${id}/claim/${entry.id}`, { version: entry.version, fingerprint: entry.fingerprint, sourceVerified: true }, { timeout: 15000 })).data;
}

export async function attestOwnerRequest(id, evidence) {
  if (previewMode) throw new Error('תיעוד בקשת בעלים אינו זמין בהדגמה');
  return (await axios.post(`/api/inquiries/owner-requests/${id}/attest`, evidence, { timeout: 15000 })).data;
}

function requirePolicySystem() {
  if (previewMode) throw new Error('מדיניות והרשאות זמינות רק במערכת המאומתת');
}
export async function listAutomaticPolicies() {
  requirePolicySystem();
  return (await axios.get('/api/inquiries/automatic-policies', { timeout: 15000 })).data;
}
export async function registerAutomaticPolicy(definition) {
  requirePolicySystem();
  return (await axios.post('/api/inquiries/automatic-policies', definition, { timeout: 15000 })).data;
}
export async function readAutomaticPolicy(id) {
  requirePolicySystem();
  return (await axios.get(`/api/inquiries/automatic-policies/${id}`, { timeout: 15000 })).data;
}
export async function attestAutomaticPolicy(id, evidence) {
  requirePolicySystem();
  return (await axios.post(`/api/inquiries/automatic-policies/${id}/attest-activation`, evidence, { timeout: 15000 })).data;
}
export async function deactivateAutomaticPolicy(policy, reason) {
  requirePolicySystem();
  return (await axios.post(`/api/inquiries/automatic-policies/${policy._id}/deactivate`, { version: policy.version, reason }, { timeout: 15000 })).data;
}
export async function claimAutomaticPolicy(policy, item) {
  requirePolicySystem();
  return (await axios.post(`/api/inquiries/automatic-policies/${policy._id}/claim/${item._id}`, { version: item.version, policyVersion: policy.version, sourceVerified: true }, { timeout: 15000 })).data;
}
